import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Compass, ScrollText, Trophy, LogOut, Flame, Settings as SettingsIcon, Mail, Sparkles, Rocket, Target, Briefcase } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";

export default function TopBar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        if (!user) return;
        let alive = true;
        const load = () => {
            api.get("/notes/unread-count")
                .then((r) => {
                    if (alive) setUnread(r.data.unread || 0);
                })
                .catch(() => {});
        };
        load();
        const t = setInterval(load, 60000);
        return () => {
            alive = false;
            clearInterval(t);
        };
    }, [user]);

    if (!user) return null;

    const initials = (user.name || "?")
        .split(" ")
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    const linkClass = ({ isActive }) =>
        `inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-display font-semibold transition-all ${
            isActive
                ? "bg-slate-900 text-cream"
                : "text-slate-700 hover:bg-white/70"
        }`;

    return (
        <header className="fixed top-0 left-0 right-0 z-40 px-4 sm:px-8 py-4">
            <div className="crystal rounded-full px-3 py-2 flex items-center justify-between max-w-6xl mx-auto">
                <Link to="/atlas" data-testid="brand-link" className="flex items-center gap-2 px-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-humanities flex items-center justify-center">
                        <Compass className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-display font-black tracking-tighter text-slate-900 text-lg">
                        Project Atlas
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    <NavLink to="/atlas" className={linkClass} data-testid="nav-atlas">
                        <Compass className="w-4 h-4" /> Atlas
                    </NavLink>
                    <NavLink to="/interests" className={linkClass} data-testid="nav-interests">
                        <Flame className="w-4 h-4" /> My Interests
                    </NavLink>
                    <NavLink to="/projects" className={linkClass} data-testid="nav-projects">
                        <Rocket className="w-4 h-4" /> Projects
                    </NavLink>
                    <NavLink to="/careers" className={linkClass} data-testid="nav-careers">
                        <Briefcase className="w-4 h-4" /> Careers
                    </NavLink>
                    <NavLink to="/quests" className={linkClass} data-testid="nav-quests">
                        <Trophy className="w-4 h-4" /> Quests
                    </NavLink>
                    <NavLink to="/matchmaker" className={linkClass} data-testid="nav-matchmaker">
                        <Sparkles className="w-4 h-4" /> Matchmaker
                    </NavLink>
                    <NavLink to="/inbox" className={linkClass} data-testid="nav-inbox">
                        <span className="relative inline-flex">
                            <Mail className="w-4 h-4" />
                            {unread > 0 && (
                                <span
                                    className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-humanities text-white text-[9px] font-black flex items-center justify-center"
                                    data-testid="inbox-unread-badge"
                                >
                                    {unread > 9 ? "9+" : unread}
                                </span>
                            )}
                        </span>
                        Inbox
                    </NavLink>
                    <NavLink to="/settings" className={linkClass} data-testid="nav-settings">
                        <SettingsIcon className="w-4 h-4" /> Settings
                    </NavLink>
                </nav>

                {/* Mobile icon-only nav */}
                <nav className="flex md:hidden items-center gap-0.5">
                    {[
                        { to: "/atlas", icon: Compass, test: "nav-atlas-mobile", label: "Atlas" },
                        { to: "/interests", icon: Flame, test: "nav-interests-mobile", label: "Interests" },
                        { to: "/projects", icon: Rocket, test: "nav-projects-mobile", label: "Projects" },
                        { to: "/careers", icon: Briefcase, test: "nav-careers-mobile", label: "Careers" },
                        { to: "/bounties", icon: Target, test: "nav-bounties-mobile", label: "Bounties" },
                        { to: "/quests", icon: Trophy, test: "nav-quests-mobile", label: "Quests" },
                        { to: "/matchmaker", icon: Sparkles, test: "nav-matchmaker-mobile", label: "Matchmaker" },
                        { to: "/inbox", icon: Mail, test: "nav-inbox-mobile", label: "Inbox", badge: unread },
                        { to: "/profile", icon: ScrollText, test: "nav-profile-mobile", label: "Ledger" },
                        { to: "/settings", icon: SettingsIcon, test: "nav-settings-mobile", label: "Settings" },
                    ].map(({ to, icon: Icon, test, label, badge }) => (
                        <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) =>
                                `relative p-2 rounded-full transition-all ${
                                    isActive ? "bg-slate-900 text-cream" : "text-slate-700 hover:bg-white/70"
                                }`
                            }
                            data-testid={test}
                            title={label}
                        >
                            <Icon className="w-4 h-4" />
                            {badge > 0 && (
                                <span className="absolute top-0 right-0 min-w-[14px] h-[14px] px-1 rounded-full bg-humanities text-white text-[8px] font-black flex items-center justify-center">
                                    {badge > 9 ? "9+" : badge}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <Link to="/profile" className="hidden sm:flex items-center gap-2 pr-2 hover:opacity-80 transition-opacity" data-testid="topbar-user-link">
                        <div
                            className="w-8 h-8 rounded-full flex items-center justify-center font-display font-bold text-white text-sm"
                            style={{ background: `linear-gradient(135deg, ${BRANCH_META.arts.color}, ${BRANCH_META.humanities.color})` }}
                            data-testid="topbar-avatar"
                        >
                            {initials}
                        </div>
                        <div className="text-xs leading-tight">
                            <div className="font-display font-bold text-slate-900" data-testid="topbar-username">{user.name}</div>
                            <div className="text-slate-500">Explorer · Lv {user.level}</div>
                        </div>
                    </Link>
                    <button
                        onClick={async () => {
                            await logout();
                            nav("/");
                        }}
                        className="arcade-btn"
                        data-variant="ghost"
                        data-testid="logout-btn"
                        title="Sign out"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </header>
    );
}
